import React, { useEffect, useState } from 'react';
import { EyeOff, SearchCode, RotateCcw, LoaderCircle, Undo2, Search } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useActivityRetry, useActivityRollback, useRemedActivity } from '../services/activityService';
import { useAuth } from 'oidc-react';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';

type Activity = {
  id: number;
  appIcon: string;
  activity: string;
  patches: number;
  endpoints: number;
  failedDevices: number;
  successPercentage: number;
  agentIds: string[];
  failedAgents: number;
  approvedOn: string | null;
};

const BADGE_STOP_WORDS = new Set(['for', 'the', 'a', 'an', 'of', 'and', 'to', 'on', 'in']);

// Derives a short badge (e.g. "RA") from a plan/activity name by splitting
// camelCase/hyphens/underscores into words and dropping dates, times, and stop words.
const getActivityBadge = (activity: string): string => {
  if (!activity) return 'NA';

  const spaced = activity.replace(/([a-z])([A-Z])/g, '$1 $2');
  const rawTokens = spaced.split(/[\s_-]+/).filter(Boolean);

  const words: string[] = [];
  for (const token of rawTokens) {
    if (/^\d+$/.test(token)) continue; // dates like 2026, 07, 10
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(token)) continue; // times like 16:24:50
    if (BADGE_STOP_WORDS.has(token.toLowerCase())) continue;
    if (!/[a-zA-Z]/.test(token)) continue;
    if (words.length && words[words.length - 1].toLowerCase() === token.toLowerCase()) continue; // adjacent duplicates
    words.push(token);
  }

  if (words.length === 0) return 'NA';

  return words
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
};

// Renders an ISO date string in the viewer's browser-local timezone.
const formatApprovedOn = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

const ITEMS_PER_PAGE = 20;

const ActivityTable: React.FC = ({ mode }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [rollbackLoadingId, setRollbackLoadingId] = useState<number | null>(null);
  const [retryLoadingId, setRetryLoadingId] = useState<number | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { getRemedActivity } = useRemedActivity();
  const { getRollbackActivity } = useActivityRollback();
  const { retryActivity } = useActivityRetry();
  const { addToast } = useToast();
  const tenantID = selectedTenantId as string;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statuses = queryParams.get('action') ?? undefined;
  const groupId = queryParams.get('groupId') ?? undefined;
  const groupPolicyAutomationId = queryParams.get('groupPolicyAutomationId') ?? undefined;

  useEffect(() => {
    if (!tenantID) return;

    const fetchActivities = async () => {
      try {
        setLoading(true);
        const response = await getRemedActivity(tenantID, currentPage, ITEMS_PER_PAGE, mode, statuses, groupId, groupPolicyAutomationId);
        const mappedData: Activity[] = response.data.map((item: any, index: number) => {
          const activityName = item.planName || item.activity || 'N/A';
          return {
            id: item.remediationPlanID,
            appIcon: item.appIcon || getActivityBadge(activityName),
            activity: activityName,
            patches: item.remediationPlanPatchCount ?? 0,
            endpoints: item.totalAgent ?? 0,
            failedDevices: item.completedWithErrorsAgent ?? 0,
            failedAgents: item.failedAgents ?? 0,
            successPercentage: item.totalAgent ? Math.round((item.completedAgent / item.totalAgent) * 100) : 0,
            agentIds: item.remediationPlanAgentIDs ?? [],
            approvedOn: item.approvedOn ?? null,
          };
        });

        setActivities(mappedData);
        setPaginationInfo(response.pagination ?? null);
      } catch (error) {
        console.error('Error fetching remediation activities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [tenantID, currentPage, mode]);

  const totalPages = paginationInfo?.totalPages ?? 1;

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  // ✅ rollback handler with toast
  const handleRollback = async (remediationPlanId: number, agentIds: string[]) => {
    try {
      setRollbackLoadingId(remediationPlanId);
      const res = await getRollbackActivity(tenantID, remediationPlanId.toString(), agentIds);
      console.log('Rollback success:', res);

      addToast('Rollback initiated successfully for the selected remediation plan.', 'success', 4000, 'Rollback Successful');
    } catch (error: any) {
      console.error('Rollback failed:', error);
    } finally {
      setRollbackLoadingId(null);
    }
  };

  // ✅ retry handler with toast
  const handleRetry = async (remediationPlanId: number) => {
    try {
      setRetryLoadingId(remediationPlanId);
      const res = await retryActivity(remediationPlanId.toString());
      console.log('Retry success:', res);

      addToast(`${res.message}`, 'success', 4000, 'Retry');
    } catch (error: any) {
      console.error('Retry failed:', error);
    } finally {
      setRetryLoadingId(null);
    }
  };

  if (loading) {
    return <LoaderCircle className="w-8 h-8 mx-auto animate-spin text-gray-400" />;
  }

  return (
    <div className="text-white rounded-lg" style={{ background: '#1D1D1D' }}>
      {activities.length === 0 ? (
        <div className="p-4 text-gray-400">No activities found</div>
      ) : (
        <>
          <table className="min-w-full text-left">
            <thead>
              <tr className="font-normal text-sm" style={{ background: '#161617' }}>
                <th className="px-4 py-2">Activities</th>
                <th className="px-4 py-2">Number of Patches</th>
                <th className="px-4 py-2">Number of Endpoints</th>
                <th className="px-4 py-2">Number of Devices Failed</th>
                <th className="px-4 py-2">Initiated On</th>
                <th className="px-4 py-2">Success Percentage</th>
                <th className="px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((item, index) => (
                <tr key={item.id} className="border-b border-gray-700" style={{ background: index % 2 === 0 ? '#232222' : '#161617' }}>
                  <td className="px-4 py-2 flex items-center gap-2">
                    <span className="bg-gray-700 text-xs px-2 py-1 rounded-full">{item.appIcon}</span>
                    {item.activity}
                  </td>
                  <td className="px-4 py-2">{item.patches}</td>
                  <td className="px-4 py-2">{item.endpoints}</td>
                  <td className="px-4 py-2">{item.failedAgents}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-300">{formatApprovedOn(item.approvedOn)}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-600 h-2 rounded">
                        <div className="bg-green-500 h-2 rounded" style={{ width: `${item.successPercentage}%` }} />
                      </div>
                      <span className="text-sm text-gray-300">{item.successPercentage}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <button
                        title="View"
                        className="bg-[#2f2f2f] p-2 rounded"
                        onClick={() => navigate(`/patchcenter/RemediationReport/${item.id}?${searchParams.toString()}`)}
                      >
                        <Search size={20} className="text-white" />
                      </button>
                      {/* ✅ Retry Button */}
                      {mode !== 'Automated' && (
                        <button
                          title="Retry"
                          className={`p-2 rounded ${
                            retryLoadingId === item.id || item.successPercentage === 100
                              ? 'bg-[#1a1a1a] cursor-not-allowed'
                              : 'bg-[#2f2f2f]'
                          }`}
                          onClick={() => handleRetry(item.id)}
                          disabled={retryLoadingId === item.id || item.successPercentage === 100}
                        >
                          {retryLoadingId === item.id ? (
                            <LoaderCircle size={20} className="text-blue-500 animate-spin" />
                          ) : (
                            <RotateCcw size={20} className={item.successPercentage === 100 ? 'text-gray-600' : 'text-blue-500'} />
                          )}
                        </button>
                      )}
                      {/* ✅ Rollback Button */}
                      {mode !== 'Automated' && (
                        <button
                          title="Rollback"
                          className="bg-[#2f2f2f] p-2 rounded"
                          onClick={() => handleRollback(item.id, item.agentIds)}
                          disabled={rollbackLoadingId === item.id}
                        >
                          {rollbackLoadingId === item.id ? (
                            <LoaderCircle size={20} className="text-yellow-500 animate-spin" />
                          ) : (
                            <Undo2 size={20} className="text-yellow-500" />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ✅ Backend Pagination Component */}
          <Pagination currentPage={currentPage} totalPages={totalPages} loading={loading} onPageChange={handlePageChange} />
        </>
      )}
    </div>
  );
};

export default ActivityTable;
